import React, { useEffect, useState } from "react";
import {
  openTill,
  fetchCurrentTill,
  closeTill,
  getCurrency,
  getVenueType,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { printDocument } from "../utils/printDoc";
import { useT } from "../i18n/useT";

// MyTillPage (cashier-facing) — one cashier's own till / cash drawer.
// No open till → an "Open till" form (point + opening float). An open till →
// its running totals + a "Close till" flow (count cash, see the live variance,
// give a reason when non-zero) → then the printable till Z. Mirrors the local
// trilingual `S` table + formatPrice conventions of FinanceReportsPage.
const S = {
  en: {
    title: "My till",
    subtitle: "Open a drawer, ring sales, close with a cash count",
    // open form
    open_h: "Open a till",
    point: "Sales point",
    pt_front_desk: "Front desk", pt_fnb: "Food & Beverage", pt_wellness: "Wellness", pt_events: "Events",
    opening_float: "Opening cash float",
    open_btn: "Open till",
    // open till
    open_till_h: "Till open",
    since: "Opened", cashier: "Cashier",
    sales_so_far: "Sales so far", txns: "transactions",
    cash_collected: "Cash collected", cash_expected: "Cash expected (float + cash sales)",
    by_method: "By payment method", method: "Method", amount: "Amount", count: "Count",
    // close
    close_h: "Close till",
    counted_cash: "Counted cash", counted_ph: "Count the drawer",
    variance: "Variance", reason: "Reason for variance", reason_ph: "Why doesn't it reconcile?",
    reason_req: "A reason is required when the cash does not reconcile.",
    close_btn: "Close till",
    // z
    z_h: "Till Z-read", print: "Print", opening: "Opening float",
    print_format: "Print format", fmt_a4: "A4", fmt_thermal: "Thermal 80mm",
    expected: "Expected cash", counted: "Counted cash",
    by_point: "Sales by point", point_c: "Point", gross: "Gross",
    total: "Total", new_till: "Open a new till",
    // states
    loading: "Loading…", none: "Nothing rung on this till yet.",
    err: "Something went wrong. Please try again.",
  },
  am: {
    // TODO verify
    title: "የእኔ ገንዘብ ሳጥን",
    subtitle: "Open a drawer, ring sales, close with a cash count",
    open_h: "Open a till",
    point: "Sales point",
    pt_front_desk: "Front desk", pt_fnb: "Food & Beverage", pt_wellness: "Wellness", pt_events: "Events",
    opening_float: "Opening cash float",
    open_btn: "Open till",
    open_till_h: "Till open",
    since: "Opened", cashier: "Cashier",
    sales_so_far: "Sales so far", txns: "transactions",
    cash_collected: "Cash collected", cash_expected: "Cash expected (float + cash sales)",
    by_method: "By payment method", method: "Method", amount: "Amount", count: "Count",
    close_h: "Close till",
    counted_cash: "Counted cash", counted_ph: "Count the drawer",
    variance: "Variance", reason: "Reason for variance", reason_ph: "Why doesn't it reconcile?",
    reason_req: "A reason is required when the cash does not reconcile.",
    close_btn: "Close till",
    z_h: "Till Z-read", print: "Print", opening: "Opening float",
    print_format: "Print format", fmt_a4: "A4", fmt_thermal: "Thermal 80mm",
    expected: "Expected cash", counted: "Counted cash",
    by_point: "Sales by point", point_c: "Point", gross: "Gross",
    total: "Total", new_till: "Open a new till",
    loading: "Loading…", none: "Nothing rung on this till yet.",
    err: "Something went wrong. Please try again.",
  },
  om: {
    // TODO verify
    title: "Kaassaa koo",
    subtitle: "Open a drawer, ring sales, close with a cash count",
    open_h: "Open a till",
    point: "Sales point",
    pt_front_desk: "Front desk", pt_fnb: "Food & Beverage", pt_wellness: "Wellness", pt_events: "Events",
    opening_float: "Opening cash float",
    open_btn: "Open till",
    open_till_h: "Till open",
    since: "Opened", cashier: "Cashier",
    sales_so_far: "Sales so far", txns: "transactions",
    cash_collected: "Cash collected", cash_expected: "Cash expected (float + cash sales)",
    by_method: "By payment method", method: "Method", amount: "Amount", count: "Count",
    close_h: "Close till",
    counted_cash: "Counted cash", counted_ph: "Count the drawer",
    variance: "Variance", reason: "Reason for variance", reason_ph: "Why doesn't it reconcile?",
    reason_req: "A reason is required when the cash does not reconcile.",
    close_btn: "Close till",
    z_h: "Till Z-read", print: "Print", opening: "Opening float",
    print_format: "Print format", fmt_a4: "A4", fmt_thermal: "Thermal 80mm",
    expected: "Expected cash", counted: "Counted cash",
    by_point: "Sales by point", point_c: "Point", gross: "Gross",
    total: "Total", new_till: "Open a new till",
    loading: "Loading…", none: "Nothing rung on this till yet.",
    err: "Something went wrong. Please try again.",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const TH = { textAlign: "left", color: "#5b6472", padding: "3px 6px" };
const TR = { textAlign: "right" };
const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2" };

export default function MyTillPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (v) => formatPrice(v || 0, cur);

  // Sales points depend on the venue. A gym/spa only rings up wellness, so
  // there's no reason to show Front desk / F&B / Events on its till.
  const venue = getVenueType();
  const POINTS = venue === "spa_gym"
    ? [["wellness", "pt_wellness"]]
    : [["front_desk", "pt_front_desk"], ["fnb", "pt_fnb"], ["wellness", "pt_wellness"], ["events", "pt_events"]];

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [till, setTill] = useState(null);       // open till + totals, or null
  const [z, setZ] = useState(null);             // till Z after close

  // open form
  const [point, setPoint] = useState(POINTS[0][0]);
  const [openingFloat, setOpeningFloat] = useState("");
  const [opening, setOpening] = useState(false);

  // close form
  const [countedCash, setCountedCash] = useState("");
  const [reason, setReason] = useState("");
  const [closing, setClosing] = useState(false);

  // A till Z is a cashier receipt, so default to the thermal 80mm roll.
  const [printFormat, setPrintFormat] = useState("thermal");

  const load = () => {
    setLoading(true);
    setErr("");
    fetchCurrentTill(point)
      .then((d) => setTill(d && d.till ? d.till : null))
      .catch(() => setErr(t("err")))
      .finally(() => setLoading(false));
  };
  // On mount and whenever the selected sales point changes, load that point's
  // open till (if any). Clearing any after-close Z so the selector always shows
  // the live state of the point it now points at.
  useEffect(() => { setZ(null); load(); /* eslint-disable-next-line */ }, [point]);

  const doOpen = () => {
    setErr("");
    setOpening(true);
    openTill({ point, openingFloat })
      .then(() => { setOpeningFloat(""); setZ(null); load(); })
      .catch((e) => setErr(e?.response?.data?.detail || t("err")))
      .finally(() => setOpening(false));
  };

  // Live variance while counting (counted - expected). Expected = float + cash.
  const expected = till ? Number(till.expected_cash || 0) : 0;
  const counted = countedCash === "" ? null : Number(countedCash);
  const variance = counted == null ? null : Math.round((counted - expected) * 100) / 100;
  const varianceOff = variance != null && Math.abs(variance) >= 0.01;

  const doClose = () => {
    if (varianceOff && !reason.trim()) { setErr(t("reason_req")); return; }
    setErr("");
    setClosing(true);
    closeTill(till.id, { countedCash: counted || 0, varianceReason: reason.trim() || undefined })
      .then((zr) => { setZ(zr); setTill(null); setCountedCash(""); setReason(""); })
      .catch((e) => setErr(e?.response?.data?.detail || t("err")))
      .finally(() => setClosing(false));
  };

  // Print the till Z via the shared print helper, sized for the chosen device
  // (thermal 80mm receipt roll by default, or an A4 sheet).
  const printZ = () => {
    if (!z) return;
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const tbl = (headers, rows) =>
      `<table style="width:100%;border-collapse:collapse;font-size:12px;margin:6px 0">`
      + `<thead><tr>${headers.map((h) => `<th style="text-align:left;border-bottom:1px solid #999;padding:3px 6px">${esc(h)}</th>`).join("")}</tr></thead>`
      + `<tbody>${rows.map((r) => `<tr>${r.map((c) => `<td style="padding:2px 6px;border-bottom:1px solid #eee">${esc(c)}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
    let body = "";
    body += tbl(
      [t("cashier"), t("point"), t("opening"), t("expected"), t("counted"), t("variance")],
      [[z.cashier_staff_id ?? "-", z.point || "-", money(z.opening_float), money(z.expected_cash), money(z.counted_cash), money(z.cash_variance)]]
    );
    body += `<h3>${esc(t("by_point"))}</h3>`
      + tbl([t("point_c"), t("gross"), t("count")], (z.by_revenue_point || []).map((p) => [p.point, money(p.gross), p.count]));
    body += `<h3>${esc(t("by_method"))}</h3>`
      + tbl([t("method"), t("amount"), t("count")], (z.by_payment_method || []).map((m) => [m.method, money(m.amount), m.count]));
    const tot = z.totals || {};
    body += `<h3>${esc(t("total"))}</h3>`
      + tbl([t("gross"), t("count")], [[money(tot.gross), tot.count]]);
    const bodyHtml =
      `<h2>${esc(t("z_h"))}</h2>`
      + `<div style="color:#666;margin-bottom:8px">${esc(t("since"))}: ${esc((z.opened_at || "").slice(0, 16).replace("T", " "))} → ${esc((z.closed_at || "").slice(0, 16).replace("T", " "))}</div>${body}`;
    printDocument({ title: t("z_h"), bodyHtml, format: printFormat });
  };

  const varColor = (v) => (v == null ? "#5b6472" : Math.abs(v) < 0.01 ? "#0F6E56" : "#A32D2D");

  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      <h1 style={{ margin: "6px 0 2px" }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}

      {/* Sales point selector — switching it switches which point's till you
          see/open, so a manager can move between points instead of being
          locked to one. Hidden when the venue has a single point (e.g. gym). */}
      {POINTS.length > 1 && (
      <div style={{ ...CARD, padding: "12px 16px", marginTop: 14, maxWidth: 420 }}>
        <label style={{ fontSize: 12, color: "#5f6b78", display: "block" }}>{t("point")}<br />
          <select style={{ ...inp, width: "100%", marginTop: 4 }} value={point} onChange={(e) => setPoint(e.target.value)}>
            {POINTS.map(([val, key]) => <option key={val} value={val}>{t(key)}</option>)}
          </select>
        </label>
      </div>
      )}

      {loading ? (
        <p className="muted" style={{ marginTop: 16 }}>{t("loading")}</p>
      ) : till ? (
        /* ---------------- OPEN TILL: totals + close flow ---------------- */
        <>
          <div style={{ ...CARD, padding: 16, marginTop: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{t("open_till_h")}</div>
              <div className="muted" style={{ fontSize: 13 }}>
                {t("point")}: <b>{till.point}</b> · {t("since")} {(till.opened_at || "").slice(0, 16).replace("T", " ")}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginTop: 14 }}>
              <Stat label={t("opening")} value={money(till.opening_float)} />
              <Stat label={t("sales_so_far")} value={money((till.totals || {}).gross_total)} sub={`${(till.totals || {}).count || 0} ${t("txns")}`} />
              <Stat label={t("cash_collected")} value={money((till.totals || {}).cash_collected)} />
              <Stat label={t("cash_expected")} value={money(till.expected_cash)} accent />
            </div>

            {(till.totals && till.totals.by_payment_method && till.totals.by_payment_method.length > 0) ? (
              <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse", marginTop: 14 }}>
                <thead><tr><th style={TH}>{t("method")}</th><th style={{ ...TH, ...TR }}>{t("count")}</th><th style={{ ...TH, ...TR }}>{t("amount")}</th></tr></thead>
                <tbody>
                  {till.totals.by_payment_method.map((m) => (
                    <tr key={m.method}>
                      <td style={{ padding: "3px 6px", fontWeight: 600 }}>{m.method}</td>
                      <td style={TR}>{m.count}</td>
                      <td style={TR}>{money(m.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="muted" style={{ fontSize: 13, marginTop: 12 }}>{t("none")}</div>
            )}
          </div>

          {/* Close flow */}
          <div style={{ ...CARD, padding: 16, marginTop: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 10 }}>{t("close_h")}</div>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
              <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("counted_cash")}<br />
                <input type="number" min="0" step="0.01" style={{ ...inp, width: 160 }}
                  value={countedCash} placeholder={t("counted_ph")}
                  onChange={(e) => setCountedCash(e.target.value)} />
              </label>
              <div style={{ fontSize: 12, color: "#5f6b78" }}>{t("variance")}<br />
                <span style={{ fontSize: 20, fontWeight: 800, color: varColor(variance) }}>
                  {variance == null ? "—" : money(variance)}
                </span>
              </div>
            </div>
            {varianceOff && (
              <label style={{ fontSize: 12, color: "#5f6b78", display: "block", marginTop: 12 }}>{t("reason")}<br />
                <input type="text" style={{ ...inp, width: "100%", maxWidth: 420 }}
                  value={reason} placeholder={t("reason_ph")}
                  onChange={(e) => setReason(e.target.value)} />
              </label>
            )}
            <div style={{ marginTop: 14 }}>
              <button className="btn primary" onClick={doClose} disabled={closing || countedCash === ""}>
                {t("close_btn")}
              </button>
            </div>
          </div>
        </>
      ) : z ? (
        /* ---------------- TILL Z (after close) ---------------- */
        <div style={{ ...CARD, padding: 16, marginTop: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{t("z_h")}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("print_format")}{" "}
                <select style={{ ...inp, marginLeft: 4 }} value={printFormat} onChange={(e) => setPrintFormat(e.target.value)}>
                  <option value="a4">{t("fmt_a4")}</option>
                  <option value="thermal">{t("fmt_thermal")}</option>
                </select>
              </label>
              <button className="btn secondary small" onClick={printZ}>{t("print")}</button>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginTop: 14 }}>
            <Stat label={t("opening")} value={money(z.opening_float)} />
            <Stat label={t("expected")} value={money(z.expected_cash)} />
            <Stat label={t("counted")} value={money(z.counted_cash)} />
            <Stat label={t("variance")} value={money(z.cash_variance)} varColor={varColor(z.cash_variance)} />
          </div>
          {z.variance_reason && <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>{t("reason")}: {z.variance_reason}</div>}

          <div style={{ fontWeight: 700, margin: "16px 0 6px" }}>{t("by_point")}</div>
          <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
            <thead><tr><th style={TH}>{t("point_c")}</th><th style={{ ...TH, ...TR }}>{t("count")}</th><th style={{ ...TH, ...TR }}>{t("gross")}</th></tr></thead>
            <tbody>
              {(z.by_revenue_point || []).map((p) => (
                <tr key={p.point}>
                  <td style={{ padding: "3px 6px", fontWeight: 600 }}>{p.point}</td>
                  <td style={TR}>{p.count}</td>
                  <td style={TR}>{money(p.gross)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: "1px solid #ddd", fontWeight: 700 }}>
                <td style={{ padding: "4px 6px" }}>{t("total")} ({(z.totals || {}).count})</td>
                <td />
                <td style={TR}>{money((z.totals || {}).gross)}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ fontWeight: 700, margin: "16px 0 6px" }}>{t("by_method")}</div>
          <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
            <thead><tr><th style={TH}>{t("method")}</th><th style={{ ...TH, ...TR }}>{t("count")}</th><th style={{ ...TH, ...TR }}>{t("amount")}</th></tr></thead>
            <tbody>
              {(z.by_payment_method || []).map((m) => (
                <tr key={m.method}>
                  <td style={{ padding: "3px 6px", fontWeight: 600 }}>{m.method}</td>
                  <td style={TR}>{m.count}</td>
                  <td style={TR}>{money(m.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: 16 }}>
            <button className="btn secondary" onClick={() => setZ(null)}>{t("new_till")}</button>
          </div>
        </div>
      ) : (
        /* ---------------- NO OPEN TILL: open form ---------------- */
        <div style={{ ...CARD, padding: 16, marginTop: 14, maxWidth: 420 }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>{t("open_h")}</div>
          <label style={{ fontSize: 12, color: "#5f6b78", display: "block" }}>{t("opening_float")}<br />
            <input type="number" min="0" step="0.01" style={{ ...inp, width: "100%", marginTop: 4 }}
              value={openingFloat} placeholder="0.00"
              onChange={(e) => setOpeningFloat(e.target.value)} />
          </label>
          <div style={{ marginTop: 16 }}>
            <button className="btn primary" onClick={doOpen} disabled={opening}>{t("open_btn")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, accent, varColor }) {
  return (
    <div style={{ background: accent ? "#E7F6EC" : "#f7f9fa", borderRadius: 10, padding: "10px 12px" }}>
      <div style={{ fontSize: 11.5, color: "#5f6b78", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: varColor || (accent ? "#0F6E56" : "#12303a") }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11.5 }}>{sub}</div>}
    </div>
  );
}
